import { useState, useEffect, useRef } from 'react';
import './ImportPanel.css';
import { supabase } from '../supabaseClient';
import ImageUpload from './ImageUpload';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import { useAuth } from '../AuthContext';

const CATEGORIES = ['Main Course', 'Soup', 'Dessert', 'Baking', 'Breakfast', 'Starters', 'Side Dishes'];
const UNITS = ['', 'g', 'kg', 'ml', 'L', 'tbsp', 'tsp', 'count'];
const COMMON_TAGS = ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'nut-free', 'low-carb', 'keto'];

// ─── Tag chip editor ─────────────────────────────────────────────────────────

function TagEditor({ tags, onChange, placeholder }) {
  const [input, setInput] = useState('');

  function addTag(raw) {
    const tag = raw.trim().toLowerCase();
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setInput('');
  }

  function removeTag(tag) {
    onChange(tags.filter((t) => t !== tag));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && tags.length) {
      removeTag(tags[tags.length - 1]);
    }
  }

  return (
    <div className="tag-editor">
      <div className="tag-editor__chips">
        {tags.map((tag) => (
          <span key={tag} className="tag-chip">
            {tag}
            <button type="button" className="tag-chip__remove" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>×</button>
          </span>
        ))}
        {COMMON_TAGS.filter((t) => !tags.includes(t)).map((t) => (
          <button key={t} type="button" className="tag-chip tag-chip--add" onClick={() => onChange([...tags, t])}>
            + {t}
          </button>
        ))}
      </div>
      <input
        className="tag-editor__input"
        type="text"
        value={input}
        placeholder={placeholder}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) addTag(input); }}
      />
    </div>
  );
}

// ─── Helper: normalise a Claude response into form state ─────────────────────

function normaliseRecipe(recipe, sourceUrl = '') {
  return {
    title: recipe.title || '',
    description: recipe.description || '',
    category: CATEGORIES.includes(recipe.category) ? recipe.category : 'Main Course',
    cuisine: recipe.cuisine || '',
    difficulty: ['easy', 'medium', 'hard'].includes(recipe.difficulty) ? recipe.difficulty : 'medium',
    prep_time_mins: recipe.prep_time_mins ?? '',
    cook_time_mins: recipe.cook_time_mins ?? '',
    default_servings: recipe.default_servings ?? 4,
    servings_unit: recipe.servings_unit || 'servings',
    ingredients: (recipe.ingredients || []).map((ing, i) => ({ ...ing, _key: i })),
    steps: (recipe.steps || []).map((s, i) => ({ ...s, _key: i })),
    nutrition: recipe.nutrition || {
      calories: '', protein_g: '', carbs_g: '', fat_g: '',
      fiber_g: '', sugar_g: '', sodium_mg: '',
    },
    dietary_tags: recipe.dietary_tags || [],
    equipment: recipe.equipment || [],
    notes: recipe.notes || '',
    storage_instructions: recipe.storage_instructions || '',
    image_url: recipe.image_url || '',
    source_url: recipe.source_url || sourceUrl,
    is_traditional: recipe.is_traditional || false,
  };
}

// ─── Helper: empty form for manual entry ────────────────────────────────────

function emptyForm() {
  return {
    title: '', description: '', category: 'Main Course', cuisine: '',
    difficulty: 'medium', prep_time_mins: '', cook_time_mins: '',
    default_servings: 4, servings_unit: 'servings',
    ingredients: [], steps: [],
    nutrition: { calories: '', protein_g: '', carbs_g: '', fat_g: '', fiber_g: '', sugar_g: '', sodium_mg: '' },
    dietary_tags: [], equipment: [], notes: '', storage_instructions: '',
    image_url: '', source_url: '', is_traditional: false,
  };
}

// ─── Helper: read a File as base64 ──────────────────────────────────────────

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Main component ──────────────────────────────────────────────────────────
//
// Steps:
//   choose → url | youtube | paste | photo | file | manual
//   url / youtube / paste / photo / file → loading → preview
//   manual → preview (no loading)

function ImportPanel({ onClose, onRecipeSaved }) {
  const { language } = useLanguage();
  const ip = translations[language].importPanel;
  const t = translations[language];
  const { user, isMember, displayName } = useAuth();

  const [step, setStep] = useState('choose');
  const [sourceStep, setSourceStep] = useState('url'); // for Back button in preview + loading message lookup

  // Input values
  const [url, setUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null); // for photo or PDF

  // Result
  const [form, setForm] = useState(null);
  const [, setPantryNotice] = useState([]);
  const [generatedTips, setGeneratedTips] = useState([]);
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [huData, setHuData] = useState(null);

  // Errors & saving
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [manualLanguage, setManualLanguage] = useState('en'); // for manual/blank entry only
  const [calcNutriLoading, setCalcNutriLoading] = useState(false);
  const [calcNutriError, setCalcNutriError] = useState('');

  const urlInputRef = useRef(null);
  const youtubeInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (step === 'url' && urlInputRef.current) urlInputRef.current.focus();
    if (step === 'youtube' && youtubeInputRef.current) youtubeInputRef.current.focus();
  }, [step]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Shared: process Claude response ────────────────────────────────────────

  function processClaudeResult(data, fallbackSourceUrl = '') {
    const { pantry_staples, tips, source_language, hu, ...recipe } = data;
    setPantryNotice(pantry_staples || []);
    setGeneratedTips(tips || []);
    setSourceLanguage(source_language || 'en');
    setHuData(hu || null);
    setForm(normaliseRecipe(recipe, fallbackSourceUrl));
    setStep('preview');
  }

  // ── Import from URL ─────────────────────────────────────────────────────────

  async function handleFetchUrl(e) {
    e.preventDefault();
    if (!url.trim()) return;
    setError('');
    setSourceStep('url');
    setStep('loading');

    try {
      const res = await fetch('/.netlify/functions/import-recipe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || ip.errorUrl);
        setStep('url');
        return;
      }
      processClaudeResult(data, url.trim());
    } catch (_) {
      setError(ip.errorNetwork);
      setStep('url');
    }
  }

  // ── Import from YouTube ─────────────────────────────────────────────────────

  async function handleFetchYouTube(e) {
    e.preventDefault();
    if (!youtubeUrl.trim()) return;
    setError('');
    setSourceStep('youtube');
    setStep('loading');

    try {
      const res = await fetch('/.netlify/functions/import-youtube', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: youtubeUrl.trim() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || ip.errorYoutube);
        setStep('youtube');
        return;
      }
      processClaudeResult(data, youtubeUrl.trim());
    } catch (_) {
      setError(ip.errorNetwork);
      setStep('youtube');
    }
  }

  // ── Import from pasted text ─────────────────────────────────────────────────

  async function handleSubmitText(e) {
    e.preventDefault();
    if (!pasteText.trim()) return;
    setError('');
    setSourceStep('paste');
    setStep('loading');

    try {
      const res = await fetch('/.netlify/functions/import-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'text', text: pasteText.trim() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || ip.errorPaste);
        setStep('paste');
        return;
      }
      processClaudeResult(data);
    } catch (_) {
      setError(ip.errorNetwork);
      setStep('paste');
    }
  }

  // ── Import from photo or PDF ────────────────────────────────────────────────

  async function handleFileImport(fileType) {
    // fileType: 'image' | 'pdf'
    const file = selectedFile;
    if (!file) return;

    const MAX_SIZE = 4.5 * 1024 * 1024; // 4.5 MB
    if (file.size > MAX_SIZE) {
      setError(ip.errorFileSize);
      return;
    }

    const backStep = fileType === 'image' ? 'photo' : 'file';
    setError('');
    setSourceStep(backStep);
    setStep('loading');

    try {
      const base64 = await readFileAsBase64(file);
      const res = await fetch('/.netlify/functions/import-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: fileType, fileData: base64, mimeType: file.type }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || ip.errorFile);
        setStep(backStep);
        return;
      }
      processClaudeResult(data);
    } catch (_) {
      setError(ip.errorNetwork);
      setStep(backStep);
    }
  }

  // ── Manual entry ────────────────────────────────────────────────────────────

  function handleManual() {
    setSourceStep('choose');
    setForm(emptyForm());
    setPantryNotice([]);
    setGeneratedTips([]);
    setStep('preview');
  }

  // ── Form field helpers ──────────────────────────────────────────────────────

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setNutritionField(field, value) {
    setForm((prev) => ({ ...prev, nutrition: { ...prev.nutrition, [field]: value } }));
  }

  async function calculateNutrition() {
    if (!form || !form.ingredients || form.ingredients.length === 0) {
      setCalcNutriError('Add some ingredients first.');
      return;
    }
    setCalcNutriLoading(true);
    setCalcNutriError('');
    try {
      const res = await fetch('/.netlify/functions/calculate-nutrition', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ingredients: form.ingredients, servings: form.default_servings || 4 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      const n = data.nutrition;
      setForm((prev) => ({
        ...prev,
        nutrition: {
          calories: Math.round(n.calories),
          protein_g: Math.round(n.protein_g),
          carbs_g: Math.round(n.carbs_g),
          fat_g: Math.round(n.fat_g),
          fiber_g: Math.round(n.fiber_g),
          sugar_g: Math.round(n.sugar_g),
          sodium_mg: Math.round(n.sodium_mg),
        },
      }));
    } catch (err) {
      setCalcNutriError('Could not calculate nutrition. Try again.');
    } finally {
      setCalcNutriLoading(false);
    }
  }

  function updateIngredient(key, field, value) {
    setForm((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing) => ing._key === key ? { ...ing, [field]: value } : ing),
    }));
  }

  function removeIngredient(key) {
    setForm((prev) => ({ ...prev, ingredients: prev.ingredients.filter((ing) => ing._key !== key) }));
  }

  function addIngredient() {
    const key = Date.now();
    setForm((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { _key: key, amount: '', unit: '', ingredient_name: '', prep_notes: '' }],
    }));
  }

  function updateStep(key, field, value) {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => s._key === key ? { ...s, [field]: value } : s),
    }));
  }

  function removeStep(key) {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps
        .filter((s) => s._key !== key)
        .map((s, i) => ({ ...s, step_number: i + 1 })),
    }));
  }

  function addStep() {
    const key = Date.now();
    setForm((prev) => ({
      ...prev,
      steps: [...prev.steps, { _key: key, step_number: prev.steps.length + 1, instruction: '', duration_mins: '' }],
    }));
  }

  function addEquipment(item) {
    const t = item.trim();
    if (t && !form.equipment.includes(t)) setField('equipment', [...form.equipment, t]);
  }

  function removeEquipment(item) {
    setField('equipment', form.equipment.filter((e) => e !== item));
  }

  // ── Save to Supabase ────────────────────────────────────────────────────────

  async function handleSave() {
    setSaveError('');
    if (!form.title.trim()) {
      setSaveError(ip.errorTitleRequired);
      return;
    }
    setSaving(true);

    try {
      const { data: recipeRow, error: recipeErr } = await supabase
        .from('recipes')
        .insert({
          title: form.title.trim(),
          description: form.description || null,
          category: form.category,
          cuisine: form.cuisine || null,
          difficulty: form.difficulty,
          prep_time_mins: form.prep_time_mins !== '' ? Number(form.prep_time_mins) : null,
          cook_time_mins: form.cook_time_mins !== '' ? Number(form.cook_time_mins) : null,
          default_servings: form.default_servings !== '' ? Number(form.default_servings) : 4,
          servings_unit: form.servings_unit || 'servings',
          dietary_tags: form.dietary_tags.length ? form.dietary_tags : null,
          equipment: form.equipment.length ? form.equipment : null,
          notes: form.notes || null,
          storage_instructions: form.storage_instructions || null,
          image_url: form.image_url || null,
          source_url: form.source_url || null,
          tips: generatedTips.length ? generatedTips : null,
          is_modified: false,
          is_traditional: form.is_traditional || false,
          user_id: isMember ? user?.id : null,
          owner_name: isMember ? displayName : null,
        })
        .select()
        .single();

      if (recipeErr) throw new Error(recipeErr.message);
      const recipeId = recipeRow.id;

      const ingredients = form.ingredients
        .filter((ing) => ing.ingredient_name.trim())
        .map((ing, i) => ({
          recipe_id: recipeId,
          sort_order: i + 1,
          amount: ing.amount !== '' && ing.amount !== null ? Number(ing.amount) : null,
          unit: ing.unit || null,
          ingredient_name: ing.ingredient_name.trim(),
          prep_notes: ing.prep_notes || null,
        }));

      if (ingredients.length) {
        const { error: ingErr } = await supabase.from('recipe_ingredients').insert(ingredients);
        if (ingErr) throw new Error(ingErr.message);
      }

      const steps = form.steps
        .filter((s) => s.instruction.trim())
        .map((s, i) => ({
          recipe_id: recipeId,
          step_number: i + 1,
          instruction: s.instruction.trim(),
          duration_mins: s.duration_mins !== '' && s.duration_mins !== null ? Number(s.duration_mins) : null,
        }));

      if (steps.length) {
        const { error: stepsErr } = await supabase.from('recipe_steps').insert(steps);
        if (stepsErr) throw new Error(stepsErr.message);
      }

      const n = form.nutrition;
      const hasNutrition = Object.values(n).some((v) => v !== '' && v !== null);
      if (hasNutrition) {
        const { error: nutErr } = await supabase.from('recipe_nutrition').insert({
          recipe_id: recipeId,
          calories: n.calories !== '' ? Number(n.calories) : null,
          protein_g: n.protein_g !== '' ? Number(n.protein_g) : null,
          carbs_g: n.carbs_g !== '' ? Number(n.carbs_g) : null,
          fat_g: n.fat_g !== '' ? Number(n.fat_g) : null,
          fiber_g: n.fiber_g !== '' ? Number(n.fiber_g) : null,
          sugar_g: n.sugar_g !== '' ? Number(n.sugar_g) : null,
          sodium_mg: n.sodium_mg !== '' ? Number(n.sodium_mg) : null,
        });
        if (nutErr) throw new Error(nutErr.message);
      }

      // Auto-add to "To Try" collection (fire-and-forget)
      supabase
        .from('collections')
        .select('id')
        .eq('name', 'To Try')
        .maybeSingle()
        .then(({ data: col }) => {
          if (col?.id) {
            supabase.from('collection_recipes').insert({ collection_id: col.id, recipe_id: recipeId }).then(() => {});
          }
        });

      onRecipeSaved(recipeId);
      onClose();

      // Fire-and-forget Hungarian translation (does not block save)
      // For manual entry in Hungarian: treat the form itself as the HU source
      let finalSourceLang = sourceLanguage;
      let finalHuData = huData;
      if (sourceStep === 'manual' && manualLanguage === 'hu') {
        finalSourceLang = 'hu';
        finalHuData = {
          title: form.title,
          description: form.description,
          dietary_tags: form.dietary_tags,
          equipment: form.equipment,
          storage_instructions: form.storage_instructions,
          notes: form.notes,
          ingredients: form.ingredients.map(ing => ({ ingredient_name: ing.ingredient_name, prep_notes: ing.prep_notes })),
          steps: form.steps.map(s => ({ instruction: s.instruction })),
          tips: generatedTips,
        };
      }
      translateAndStore(recipeId, form, generatedTips, finalSourceLang, finalHuData);
    } catch (err) {
      setSaveError(`Save failed: ${err.message}`);
      setSaving(false);
    }
  }

  async function translateAndStore(recipeId, form, tips, srcLang, huRaw) {
    try {
      // If the recipe was originally in Hungarian, save the original text directly —
      // no need to translate English back to Hungarian.
      if (srcLang === 'hu' && huRaw) {
        await supabase.from('recipe_translations').upsert({
          recipe_id: recipeId,
          language_code: 'hu',
          title: huRaw.title || form.title,
          description: huRaw.description || null,
          dietary_tags: huRaw.dietary_tags?.length ? huRaw.dietary_tags : null,
          equipment: huRaw.equipment?.length ? huRaw.equipment : null,
          storage_instructions: huRaw.storage_instructions || null,
          notes: huRaw.notes || null,
          ingredients: huRaw.ingredients || null,
          steps: huRaw.steps || null,
          tips: huRaw.tips?.length ? huRaw.tips : null,
        }, { onConflict: 'recipe_id,language_code' });
        return;
      }

      // Otherwise, call the translation function (English → Hungarian)
      const res = await fetch('/.netlify/functions/translate-recipe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          dietary_tags: form.dietary_tags || [],
          equipment: form.equipment || [],
          storage_instructions: form.storage_instructions || '',
          notes: form.notes || '',
          ingredients: form.ingredients.map(ing => ({
            ingredient_name: ing.ingredient_name,
            prep_notes: ing.prep_notes,
          })),
          steps: form.steps.map(s => ({ instruction: s.instruction })),
          tips: tips,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) return; // silently fail
      await supabase.from('recipe_translations').upsert({
        recipe_id: recipeId,
        language_code: 'hu',
        title: data.title,
        description: data.description,
        dietary_tags: data.dietary_tags?.length ? data.dietary_tags : null,
        equipment: data.equipment?.length ? data.equipment : null,
        storage_instructions: data.storage_instructions || null,
        notes: data.notes || null,
        ingredients: data.ingredients,
        steps: data.steps,
        tips: data.tips,
      }, { onConflict: 'recipe_id,language_code' });
    } catch (_) {
      // silently fail — admin can translate manually from recipe page
    }
  }

  // ── Loading message derived from sourceStep ─────────────────────────────────

  const loadingMessageMap = {
    url: ip.loadingUrl,
    youtube: ip.loadingYoutube,
    paste: ip.loadingPaste,
    photo: ip.loadingPhoto,
    file: ip.loadingFile,
  };

  // ── Header title lookup ─────────────────────────────────────────────────────

  const headerTitles = {
    choose: ip.titleChoose,
    url: ip.titleUrl,
    youtube: ip.titleYoutube,
    paste: ip.titlePaste,
    photo: ip.titlePhoto,
    file: ip.titleFile,
    manual: ip.titleChoose,
    loading: ip.titleLoading,
    preview: ip.titlePreview,
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="import-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`import-panel${step === 'choose' ? ' import-panel--choose' : ''}`}>

        {/* Header */}
        <div className="import-panel__header">
          <h2 className="import-panel__title">{headerTitles[step] ?? ip.titleChoose}</h2>
          <button className="import-panel__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="import-panel__body">

          {/* ── CHOOSE ── */}
          {step === 'choose' && (
            <div className="import-choose">

              <button className="import-option" data-tooltip={ip.optionUrl} onClick={() => setStep('url')}>
                <div className="import-option__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </div>
              </button>

              <button className="import-option" data-tooltip={ip.optionYoutube} onClick={() => setStep('youtube')}>
                <div className="import-option__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                </div>
              </button>

              <button className="import-option" data-tooltip={ip.optionPhoto} onClick={() => { setSelectedFile(null); setStep('photo'); }}>
                <div className="import-option__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                </div>
              </button>

              <button className="import-option" data-tooltip={ip.optionFile} onClick={() => { setSelectedFile(null); setStep('file'); }}>
                <div className="import-option__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                </div>
              </button>

              <button className="import-option" data-tooltip={ip.optionPaste} onClick={() => setStep('paste')}>
                <div className="import-option__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                  </svg>
                </div>
              </button>

              <button className="import-option" data-tooltip={ip.optionManual} onClick={handleManual}>
                <div className="import-option__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
              </button>

            </div>
          )}

          {/* ── URL INPUT ── */}
          {step === 'url' && (
            <form className="import-url-form" onSubmit={handleFetchUrl}>
              <input
                ref={urlInputRef}
                className="form-input"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.bbcgoodfood.com/recipes/…"
                required
              />
              {error && <p className="import-error">{error}</p>}
              <div className="import-url-form__actions">
                <button type="button" className="btn btn--ghost" onClick={() => { setError(''); setStep('choose'); }}>{ip.back}</button>
                <button type="submit" className="btn btn--primary" disabled={!url.trim()}>{ip.fetchRecipe}</button>
              </div>
            </form>
          )}

          {/* ── YOUTUBE INPUT ── */}
          {step === 'youtube' && (
            <form className="import-url-form" onSubmit={handleFetchYouTube}>
              <input
                ref={youtubeInputRef}
                className="form-input"
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=…"
                required
              />
              {error && <p className="import-error">{error}</p>}
              <div className="import-url-form__actions">
                <button type="button" className="btn btn--ghost" onClick={() => { setError(''); setStep('choose'); }}>{ip.back}</button>
                <button type="submit" className="btn btn--primary" disabled={!youtubeUrl.trim()}>{ip.extractRecipe}</button>
              </div>
            </form>
          )}

          {/* ── PASTE TEXT ── */}
          {step === 'paste' && (
            <form className="import-url-form" onSubmit={handleSubmitText}>
              <textarea
                className="form-textarea import-paste-textarea"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={ip.placeholderPasteText}
                rows={10}
              />
              {error && <p className="import-error">{error}</p>}
              <div className="import-url-form__actions">
                <button type="button" className="btn btn--ghost" onClick={() => { setError(''); setStep('choose'); }}>{ip.back}</button>
                <button type="submit" className="btn btn--primary" disabled={!pasteText.trim()}>{ip.structureRecipe}</button>
              </div>
            </form>
          )}

          {/* ── PHOTO UPLOAD ── */}
          {step === 'photo' && (
            <div className="import-url-form">
              <div
                className={`import-file-zone ${selectedFile ? 'import-file-zone--selected' : ''}`}
                onClick={() => photoInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (f && f.type.startsWith('image/')) { setSelectedFile(f); setError(''); }
                  else setError(ip.errorDropImage);
                }}
              >
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (f) { setSelectedFile(f); setError(''); }
                  }}
                />
                {selectedFile ? (
                  <div className="import-file-zone__selected">
                    <span className="import-file-zone__name">{selectedFile.name}</span>
                    <span className="import-file-zone__size">({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                  </div>
                ) : (
                  <div className="import-file-zone__prompt">
                    <span>{ip.photoZonePrompt}</span>
                    <span className="import-file-zone__hint">{ip.photoZoneHint}</span>
                  </div>
                )}
              </div>
              {error && <p className="import-error">{error}</p>}
              <div className="import-url-form__actions">
                <button type="button" className="btn btn--ghost" onClick={() => { setError(''); setSelectedFile(null); setStep('choose'); }}>{ip.back}</button>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!selectedFile}
                  onClick={() => handleFileImport('image')}
                >
                  {ip.readPhoto}
                </button>
              </div>
            </div>
          )}

          {/* ── PDF UPLOAD ── */}
          {step === 'file' && (
            <div className="import-url-form">
              <div
                className={`import-file-zone ${selectedFile ? 'import-file-zone--selected' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (f && f.type === 'application/pdf') { setSelectedFile(f); setError(''); }
                  else setError(ip.errorDropPdf);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (f) { setSelectedFile(f); setError(''); }
                  }}
                />
                {selectedFile ? (
                  <div className="import-file-zone__selected">
                    <span className="import-file-zone__name">{selectedFile.name}</span>
                    <span className="import-file-zone__size">({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                  </div>
                ) : (
                  <div className="import-file-zone__prompt">
                    <span>{ip.pdfZonePrompt}</span>
                    <span className="import-file-zone__hint">{ip.pdfZoneHint}</span>
                  </div>
                )}
              </div>
              {error && <p className="import-error">{error}</p>}
              <div className="import-url-form__actions">
                <button type="button" className="btn btn--ghost" onClick={() => { setError(''); setSelectedFile(null); setStep('choose'); }}>{ip.back}</button>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!selectedFile}
                  onClick={() => handleFileImport('pdf')}
                >
                  {ip.readPdf}
                </button>
              </div>
            </div>
          )}

          {/* ── LOADING ── */}
          {step === 'loading' && (
            <div className="import-loading">
              <div className="import-loading__spinner" />
              <p>{loadingMessageMap[sourceStep] || ip.loadingUrl}</p>
              <p className="import-loading__sub">{ip.loadingSub}</p>
            </div>
          )}

          {/* ── PREVIEW FORM ── */}
          {step === 'preview' && form && (
            <div className="preview-form">

              {/* Language indicator for manual entry */}
              {sourceStep === 'manual' && (
                <div className="manual-lang-row">
                  <span className="manual-lang-label">
                    {language === 'hu' ? 'Recept nyelve:' : 'Recipe language:'}
                  </span>
                  <button
                    className={`manual-lang-btn${manualLanguage === 'en' ? ' manual-lang-btn--active' : ''}`}
                    onClick={() => setManualLanguage('en')}
                    type="button"
                  >EN</button>
                  <button
                    className={`manual-lang-btn${manualLanguage === 'hu' ? ' manual-lang-btn--active' : ''}`}
                    onClick={() => setManualLanguage('hu')}
                    type="button"
                  >HU</button>
                  {manualLanguage === 'hu' && (
                    <span className="manual-lang-note">
                      {language === 'hu' ? '✓ Magyar szöveg mentve lesz közvetlenül' : '✓ Hungarian text will be saved directly'}
                    </span>
                  )}
                </div>
              )}

              {/* Basic info */}
              <section className="form-section">
                <h3 className="form-section__title">{ip.sectionBasic}</h3>
                <div className="form-group">
                  <label className="form-label">{ip.labelTitle}</label>
                  <input className="form-input" type="text" value={form.title} onChange={(e) => setField('title', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">{ip.labelDescription}</label>
                  <textarea className="form-textarea" value={form.description} onChange={(e) => setField('description', e.target.value)} rows={3} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{ip.labelCategory}</label>
                    <select className="form-select" value={form.category} onChange={(e) => setField('category', e.target.value)}>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{ip.labelCuisine}</label>
                    <input className="form-input" type="text" value={form.cuisine} onChange={(e) => setField('cuisine', e.target.value)} placeholder={ip.placeholderCuisine} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{ip.labelDifficulty}</label>
                    <select className="form-select" value={form.difficulty} onChange={(e) => setField('difficulty', e.target.value)}>
                      <option value="easy">{ip.difficultyEasy}</option>
                      <option value="medium">{ip.difficultyMedium}</option>
                      <option value="hard">{ip.difficultyHard}</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{ip.labelPrepTime}</label>
                    <input className="form-input" type="number" min="0" value={form.prep_time_mins} onChange={(e) => setField('prep_time_mins', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{ip.labelCookTime}</label>
                    <input className="form-input" type="number" min="0" value={form.cook_time_mins} onChange={(e) => setField('cook_time_mins', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{ip.labelServings}</label>
                    <input className="form-input" type="number" min="1" value={form.default_servings} onChange={(e) => setField('default_servings', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{ip.labelServingsUnit}</label>
                    <input className="form-input" type="text" value={form.servings_unit} onChange={(e) => setField('servings_unit', e.target.value)} placeholder={ip.placeholderServingsUnit} />
                  </div>
                </div>
              </section>

              {/* Ingredients */}
              <section className="form-section">
                <h3 className="form-section__title">{ip.sectionIngredients}</h3>
                <div className="ingredients-list">
                  <div className="ingredients-list__header">
                    <span>{ip.labelAmount}</span>
                    <span>{ip.labelUnit}</span>
                    <span>{ip.labelIngredient}</span>
                    <span>{ip.labelPrepNotes}</span>
                    <span />
                  </div>
                  {form.ingredients.map((ing) => (
                    <div key={ing._key} className="ingredient-row">
                      <input
                        className="form-input" type="number" min="0" step="any"
                        value={ing.amount ?? ''}
                        onChange={(e) => updateIngredient(ing._key, 'amount', e.target.value)}
                        placeholder="—"
                      />
                      <select
                        className="form-select"
                        value={ing.unit ?? ''}
                        onChange={(e) => updateIngredient(ing._key, 'unit', e.target.value)}
                      >
                        {UNITS.map((u) => <option key={u} value={u}>{u || '—'}</option>)}
                      </select>
                      <input
                        className="form-input" type="text"
                        value={ing.ingredient_name}
                        onChange={(e) => updateIngredient(ing._key, 'ingredient_name', e.target.value)}
                        placeholder={ip.placeholderIngredient}
                      />
                      <input
                        className="form-input" type="text"
                        value={ing.prep_notes ?? ''}
                        onChange={(e) => updateIngredient(ing._key, 'prep_notes', e.target.value)}
                        placeholder={ip.placeholderPrepNotes}
                      />
                      <button type="button" className="row-delete-btn" onClick={() => removeIngredient(ing._key)} aria-label="Remove ingredient">×</button>
                    </div>
                  ))}
                </div>
                <button type="button" className="btn btn--add-row" onClick={addIngredient}>{ip.addIngredient}</button>
              </section>

              {/* Steps */}
              <section className="form-section">
                <h3 className="form-section__title">{ip.sectionSteps}</h3>
                <div className="steps-list">
                  {form.steps.map((s, idx) => (
                    <div key={s._key} className="step-row">
                      <div className="step-row__number">{idx + 1}</div>
                      <textarea
                        className="form-textarea step-row__instruction"
                        value={s.instruction}
                        onChange={(e) => updateStep(s._key, 'instruction', e.target.value)}
                        rows={2}
                        placeholder={ip.placeholderStepInstruction}
                      />
                      <div className="step-row__duration">
                        <input
                          className="form-input" type="number" min="0"
                          value={s.duration_mins ?? ''}
                          onChange={(e) => updateStep(s._key, 'duration_mins', e.target.value)}
                          placeholder={ip.placeholderMins}
                        />
                        <span className="step-row__duration-label">{ip.minsLabel}</span>
                      </div>
                      <button type="button" className="row-delete-btn" onClick={() => removeStep(s._key)} aria-label="Remove step">×</button>
                    </div>
                  ))}
                </div>
                <button type="button" className="btn btn--add-row" onClick={addStep}>{ip.addStep}</button>
              </section>

              {/* Tags & extras */}
              <section className="form-section">
                <h3 className="form-section__title">{ip.sectionTags}</h3>
                <div className="form-group">
                  <label className="form-label">{ip.labelDietaryTags}</label>
                  <TagEditor tags={form.dietary_tags} onChange={(tags) => setField('dietary_tags', tags)} placeholder={ip.placeholderTagInput} />
                </div>
                <div className="form-group">
                  <label className="traditional-toggle">
                    <input
                      type="checkbox"
                      checked={!!form.is_traditional}
                      onChange={e => setField('is_traditional', e.target.checked)}
                    />
                    <span className="traditional-toggle__icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C10.5 4.5 7 5 5 4c1 3 0 6-2 7.5 2 .5 3.5 2 4 4 1-1.5 2.5-2.5 4-2.5s3 1 4 2.5c.5-2 2-3.5 4-4C17 10 16 7 17 4c-2 1-5.5.5-5-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="2" fill="currentColor"/>
                      </svg>
                    </span>
                    <span className="traditional-toggle__label">{t.labelTraditional}</span>
                    <span className="traditional-toggle__hint">{t.traditionalHint}</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">{ip.labelEquipment}</label>
                  <div className="tag-editor">
                    <div className="tag-editor__chips">
                      {form.equipment.map((item) => (
                        <span key={item} className="tag-chip">
                          {item}
                          <button type="button" className="tag-chip__remove" onClick={() => removeEquipment(item)}>×</button>
                        </span>
                      ))}
                    </div>
                    <input
                      className="tag-editor__input"
                      type="text"
                      placeholder={ip.placeholderEquipment}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ',') && e.target.value.trim()) {
                          e.preventDefault();
                          addEquipment(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      onBlur={(e) => { if (e.target.value.trim()) { addEquipment(e.target.value); e.target.value = ''; } }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">{ip.labelNotes}</label>
                  <textarea className="form-textarea" value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={2} placeholder={ip.placeholderNotes} />
                </div>
                <div className="form-group">
                  <label className="form-label">{ip.labelStorage}</label>
                  <textarea className="form-textarea" value={form.storage_instructions} onChange={(e) => setField('storage_instructions', e.target.value)} rows={2} placeholder={ip.placeholderStorage} />
                </div>
              </section>

              {/* Nutrition */}
              <section className="form-section">
                <h3 className="form-section__title">{ip.sectionNutrition} <span className="form-section__sub">({ip.sectionNutritionSub})</span></h3>
                <div className="form-row form-row--wrap">
                  {[
                    { field: 'calories', label: ip.labelCalories },
                    { field: 'protein_g', label: ip.labelProtein },
                    { field: 'carbs_g', label: ip.labelCarbs },
                    { field: 'fat_g', label: ip.labelFat },
                    { field: 'fiber_g', label: ip.labelFibre },
                    { field: 'sugar_g', label: ip.labelSugar },
                    { field: 'sodium_mg', label: ip.labelSodium },
                  ].map(({ field, label }) => (
                    <div key={field} className="form-group form-group--narrow">
                      <label className="form-label">{label}</label>
                      <input
                        className="form-input" type="number" min="0" step="any"
                        value={form.nutrition[field] ?? ''}
                        onChange={(e) => setNutritionField(field, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button type="button" className="tips-generate-btn" onClick={calculateNutrition} disabled={calcNutriLoading}>
                    {calcNutriLoading ? 'Calculating…' : 'Calculate nutrition'}
                  </button>
                  {calcNutriError && <span style={{ fontSize: '0.85rem', color: '#c0392b' }}>{calcNutriError}</span>}
                </div>
              </section>

              {/* Image & source */}
              <section className="form-section">
                <h3 className="form-section__title">{ip.sectionImage}</h3>
                <div className="form-group">
                  <label className="form-label">{ip.labelImage}</label>
                  {form.image_url && (
                    <img src={form.image_url} alt="Preview" className="image-preview" onError={(e) => (e.target.style.display = 'none')} />
                  )}
                  <ImageUpload
                    folder="recipes/new"
                    onUploaded={(url) => setField('image_url', url)}
                  />
                  <p className="form-label" style={{ marginTop: 10 }}>{ip.labelOrPasteUrl}</p>
                  <input className="form-input" type="url" value={form.image_url} onChange={(e) => setField('image_url', e.target.value)} placeholder={ip.placeholderImageUrl} />
                </div>
                <div className="form-group">
                  <label className="form-label">{ip.labelSource}</label>
                  <input className="form-input" type="text" value={form.source_url} onChange={(e) => setField('source_url', e.target.value)} placeholder={ip.placeholderSource} />
                </div>
              </section>

              {/* Actions */}
              {saveError && <p className="import-error">{saveError}</p>}
              <div className="preview-form__actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setStep(sourceStep)}
                  disabled={saving}
                >
                  {ip.back}
                </button>
                <button type="button" className="btn btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? ip.saving : ip.saveRecipe}
                </button>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ImportPanel;
